import React from 'react';
import { Play, AlertCircle, Music2 } from 'lucide-react';
import { Video } from '@meradio/shared-types';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { playVideo } from '@/features/player/playerSlice';
import { useChannelPreview } from '@/hooks/useChannelPreview';
import { formatDate, formatDuration } from '@/utils/formatters';
import { IconButton } from '@/components/ui';

interface ChannelPreviewProps {
  channelId: string;
  channelName: string;
  enabled: boolean;
}

const SKELETON_ROW_COUNT = 5;

const ChannelPreview: React.FC<ChannelPreviewProps> = ({ channelId, channelName, enabled }) => {
  const dispatch = useAppDispatch();
  const currentVideoId = useAppSelector((state) => state.player.currentVideo?.videoId);
  const { data, isLoading, isError } = useChannelPreview(channelId, enabled);

  const handlePlay = (videoId: string, title: string, thumbnailUrl: string | undefined, publishedAt: string) => {
    const now = new Date().toISOString();
    const video: Video = {
      id: videoId,
      videoId,
      channelId,
      title,
      thumbnailUrl,
      publishedAt: new Date(publishedAt),
      dateAdded: new Date(now),
      createdAt: new Date(now),
      updatedAt: new Date(now),
    };
    dispatch(playVideo(video));
  };

  if (isLoading) {
    return (
      <div className="space-y-2 py-2">
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
          <div
            key={i}
            data-testid="channel-preview-skeleton-row"
            className="h-8 rounded bg-neutral-200 dark:bg-bg-dark-tertiary animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-meradio-dark-light dark:text-text-dark-secondary">
        <AlertCircle className="w-4 h-4" />
        <span>Preview unavailable</span>
      </div>
    );
  }

  if (!data || data.videos.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-meradio-dark-light dark:text-text-dark-secondary">
        <Music2 className="w-4 h-4" />
        <span>No recent uploads</span>
      </div>
    );
  }

  return (
    <ul
      aria-label={`${channelName} recent uploads`}
      className="divide-y divide-meradio-light dark:divide-border-dark"
    >
      {data.videos.map((track) => {
        const isPlaying = currentVideoId === track.videoId;
        return (
          <li key={track.videoId} className="flex items-center gap-3 py-2">
            {isPlaying ? (
              <span
                aria-label={`Now playing: ${track.title}`}
                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-meradio-primary/10 text-meradio-primary"
              >
                <Music2 className="w-4 h-4" />
              </span>
            ) : (
              <IconButton
                aria-label={`Play ${track.title}`}
                onClick={() => handlePlay(track.videoId, track.title, track.thumbnailUrl, track.publishedAt)}
                size="sm"
                className="flex-shrink-0 text-meradio-dark dark:text-text-dark-primary"
              >
                <Play className="w-4 h-4 fill-current" />
              </IconButton>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-meradio-dark dark:text-text-dark-primary truncate">
                {track.title}
              </p>
              <p className="text-xs text-meradio-dark-light dark:text-text-dark-secondary">
                {formatDate(track.publishedAt)}
              </p>
            </div>
            <span className="text-xs text-meradio-dark-light dark:text-text-dark-secondary flex-shrink-0">
              {formatDuration(track.durationSeconds)}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default ChannelPreview;
